import { useEffect, useState } from "react";
import {
  authHeaders,
  handleUnauthorized,
} from "@/lib/auth";

const API_BASE = process.env.REACT_APP_BACKEND_URL || "";

export default function AdminUsers() {
  const [users, setUsers] = useState([]);
  const [form, setForm] = useState({
    name: "",
    email: "",
    role: "staff",
    password: "",
  });
  const [loading, setLoading] = useState(false);
  const [updatingUserId, setUpdatingUserId] = useState("");
  const [message, setMessage] = useState("");

  async function loadUsers() {
    setMessage("");

    const res = await fetch(`${API_BASE}/api/users`, {
      headers: authHeaders(),
    });

    if (res.status === 401 || res.status === 403) {
      handleUnauthorized();
      return;
    }

    if (!res.ok) {
      setMessage("Could not load users.");
      return;
    }

    const data = await res.json();
    setUsers(data);
  }

  useEffect(() => {
    loadUsers();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  async function createUser(e) {
    e.preventDefault();
    setLoading(true);
    setMessage("");

    const res = await fetch(`${API_BASE}/api/users`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        ...authHeaders(),
      },
      body: JSON.stringify(form),
    });

    const data = await res.json().catch(() => ({}));

    if (res.status === 401 || res.status === 403) {
      handleUnauthorized();
      return;
    }

    if (!res.ok) {
      setMessage(data.detail || "Could not create user.");
      setLoading(false);
      return;
    }

    setForm({
      name: "",
      email: "",
      role: "staff",
      password: "",
    });

    setMessage("User created.");
    setLoading(false);
    loadUsers();
  }

  async function setUserActive(user, active) {
    if (!user?.id || user.role === "owner") return;

    if (!active) {
      const confirmed = window.confirm(
        `Deactivate ${user.name || user.email}? They will no longer be able to sign in.`
      );

      if (!confirmed) return;
    }

    try {
      setUpdatingUserId(user.id);
      setMessage("");

      const res = await fetch(
        `${API_BASE}/api/users/${user.id}/active`,
        {
          method: "PATCH",
          headers: {
            "Content-Type": "application/json",
            ...authHeaders(),
          },
          body: JSON.stringify({ active }),
        }
      );

      const data = await res.json().catch(() => ({}));

      if (res.status === 401 || res.status === 403) {
        handleUnauthorized();
        return;
      }

      if (!res.ok) {
        setMessage(
          data.detail ||
            `Could not ${active ? "reactivate" : "deactivate"} user.`
        );
        return;
      }

      setUsers((prev) =>
        prev.map((item) =>
          item.id === data.id ? data : item
        )
      );

      setMessage(
        active
          ? `${data.name || data.email} reactivated.`
          : `${data.name || data.email} deactivated.`
      );
    } catch (err) {
      console.error("User status update failed:", err);
      setMessage(
        `Could not ${active ? "reactivate" : "deactivate"} user.`
      );
    } finally {
      setUpdatingUserId("");
    }
  }

  return (
    <div className="min-h-screen bg-zinc-950 text-white p-6">
      <div className="max-w-3xl mx-auto">
        <h1 className="text-3xl font-bold mb-2">Users</h1>
        <p className="text-zinc-400 mb-8">
          Create staff or affiliate users for TapToTable.
        </p>

        <form
          onSubmit={createUser}
          className="bg-zinc-900 border border-zinc-800 rounded-2xl p-6 mb-8 space-y-4"
        >
          <div>
            <label className="block text-sm text-zinc-300 mb-1">
              Name
            </label>
            <input
              className="w-full rounded-xl bg-zinc-950 border border-zinc-700 px-4 py-3"
              value={form.name}
              onChange={(e) =>
                setForm((prev) => ({
                  ...prev,
                  name: e.target.value,
                }))
              }
              required
            />
          </div>

          <div>
            <label className="block text-sm text-zinc-300 mb-1">
              Email
            </label>
            <input
              type="email"
              className="w-full rounded-xl bg-zinc-950 border border-zinc-700 px-4 py-3"
              value={form.email}
              onChange={(e) =>
                setForm((prev) => ({
                  ...prev,
                  email: e.target.value,
                }))
              }
              required
            />
          </div>

          <div>
            <label className="block text-sm text-zinc-300 mb-1">
              Temporary Password
            </label>

            <input
              type="password"
              className="w-full rounded-xl bg-zinc-950 border border-zinc-700 px-4 py-3"
              value={form.password}
              onChange={(e) =>
                setForm((prev) => ({
                  ...prev,
                  password: e.target.value,
                }))
              }
              required
              minLength={8}
              placeholder="Minimum 8 characters"
            />
          </div>

          <div>
            <label className="block text-sm text-zinc-300 mb-1">
              Role
            </label>
            <select
              className="w-full rounded-xl bg-zinc-950 border border-zinc-700 px-4 py-3"
              value={form.role}
              onChange={(e) =>
                setForm((prev) => ({
                  ...prev,
                  role: e.target.value,
                }))
              }
            >
              <option value="staff">Staff</option>
              <option value="affiliate">Affiliate</option>
              <option value="owner">Owner</option>
            </select>
          </div>

          <button
            disabled={loading}
            className="rounded-xl bg-cyan-400 text-zinc-950 font-bold px-5 py-3 disabled:opacity-60"
          >
            {loading ? "Creating..." : "Create User"}
          </button>

          {message && (
            <p className="text-sm text-zinc-300">
              {message}
            </p>
          )}
        </form>

        <div className="bg-zinc-900 border border-zinc-800 rounded-2xl p-6">
          <h2 className="text-xl font-bold mb-4">
            Existing Users
          </h2>

          <div className="space-y-3">
            {users.map((user) => {
              const isOwner = user.role === "owner";
              const isActive = user.active !== false;
              const isUpdating = updatingUserId === user.id;

              return (
                <div
                  key={user.id}
                  className="border border-zinc-800 rounded-xl p-4 flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between"
                >
                  <div>
                    <div className="font-bold">
                      {user.name}
                    </div>
                    <div className="text-sm text-zinc-400">
                      {user.email}
                    </div>

                    {!isActive && (
                      <div className="mt-2 inline-flex rounded-full border border-amber-400/30 bg-amber-400/10 px-2.5 py-1 text-xs font-semibold uppercase tracking-wide text-amber-300">
                        Inactive
                      </div>
                    )}
                  </div>

                  <div className="flex flex-wrap items-center gap-3">
                    <div className="text-sm uppercase text-cyan-300">
                      {user.role}
                    </div>

                    {!isOwner && (
                      <button
                        type="button"
                        onClick={() =>
                          setUserActive(user, !isActive)
                        }
                        disabled={isUpdating}
                        className={`rounded-lg border px-3 py-2 text-sm font-semibold transition-colors disabled:cursor-not-allowed disabled:opacity-50 ${
                          isActive
                            ? "border-red-400/40 bg-red-400/10 text-red-300 hover:bg-red-400/20"
                            : "border-emerald-400/40 bg-emerald-400/10 text-emerald-300 hover:bg-emerald-400/20"
                        }`}
                      >
                        {isUpdating
                          ? "Updating..."
                          : isActive
                            ? "Deactivate"
                            : "Reactivate"}
                      </button>
                    )}
                  </div>
                </div>
              );
            })}

            {users.length === 0 && (
              <p className="text-zinc-500">
                No users yet.
              </p>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
